/**
 * SakListe - Oversikt over alle saker med filtrering og søk
 */

import React, { useState, useEffect } from 'react';
import crmApi from '../api/crm';
import './SakListe.css';

export default function SakListe({ navigerTil }) {
  const [saker, setSaker] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    status: 'alle',
    sakstype: 'alle',
    sok: ''
  });
  const [sortering, setSortering] = useState('sist_endret'); // 'sist_endret', 'opprettet', 'saksnummer', 'klient'
  const [visOppfolgingModal, setVisOppfolgingModal] = useState(false);
  const [valgteSaker, setValgteSaker] = useState(new Set());

  useEffect(() => {
    lastSaker();
  }, []);

  const lastSaker = async () => {
    try {
      setLoading(true);

      const data = await crmApi.getSaker();

      // Handle both array and object responses
      let sakArray = Array.isArray(data) ? data : (data.saker || []);

      // Sorter saker: arkiverte saker nederst
      sakArray = sakArray.sort((a, b) => {
        // Arkiverte saker nederst
        if (a.status === 'arkivert' && b.status !== 'arkivert') return 1;
        if (a.status !== 'arkivert' && b.status === 'arkivert') return -1;

        // Ellers sorter etter sist_endret (nyeste først)
        return new Date(b.sist_endret || b.opprettet_dato) - new Date(a.sist_endret || a.opprettet_dato);
      });

      setSaker(sakArray);
    } catch (error) {
      console.error('Feil ved lasting av saker:', error);
      setSaker([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilter(prev => ({ ...prev, [key]: value }));
  };

  const handleNySak = () => {
    window.location.href = '/saker/ny';
  };

  const handleSakKlikk = (sakId) => {
    if (navigerTil) {
      navigerTil('sak-detaljer', sakId);
    } else {
      window.location.href = `/saker/${sakId}`;
    }
  };

  const handleApneSharePoint = (sak) => {
    if (sak.sharepoint_web_url) {
      window.open(sak.sharepoint_web_url, '_blank');
    } else {
      alert('SharePoint-lenke ikke tilgjengelig for denne saken');
    }
  };

  const sjekkOppfolging = () => {
    const aktiveSaker = saker.filter(s => s.status === 'aktiv');
    const utenKlient = aktiveSaker.filter(s => !s.neste_oppfolging_klient_dato);
    const utenMotpart = aktiveSaker.filter(s => !s.neste_oppfolging_motpart_dato);
    const utenBegge = aktiveSaker.filter(s => !s.neste_oppfolging_klient_dato && !s.neste_oppfolging_motpart_dato);

    return {
      totaltAktive: aktiveSaker.length,
      utenKlient,
      utenMotpart,
      utenBegge,
      medFullOppfolging: aktiveSaker.filter(s => s.neste_oppfolging_klient_dato && s.neste_oppfolging_motpart_dato).length
    };
  };

  const handleVelgAlle = () => {
    if (valgteSaker.size === filtrerteOgSorterte.length) {
      // Deselekter alle
      setValgteSaker(new Set());
    } else {
      // Velg alle filtrerte
      setValgteSaker(new Set(filtrerteOgSorterte.map(s => s.id)));
    }
  };

  const handleVelgSak = (sakId) => {
    const nyeValgte = new Set(valgteSaker);
    if (nyeValgte.has(sakId)) {
      nyeValgte.delete(sakId);
    } else {
      nyeValgte.add(sakId);
    }
    setValgteSaker(nyeValgte);
  };

  const handleBulkEndreStatus = async (nyStatus) => {
    if (valgteSaker.size === 0) return;

    const bekreft = window.confirm(`Vil du endre status til "${nyStatus}" for ${valgteSaker.size} saker?`);
    if (!bekreft) return;

    try {
      const promises = Array.from(valgteSaker).map(sakId =>
        crmApi.updateSak(sakId, { status: nyStatus })
      );

      await Promise.all(promises);
      alert(`✅ Oppdaterte ${valgteSaker.size} saker til status: ${nyStatus}`);

      // Reload saker og clear selection
      await lastSaker();
      setValgteSaker(new Set());
    } catch (error) {
      console.error('Feil ved bulk-oppdatering:', error);
      alert('Kunne ikke oppdatere alle saker. Se konsoll for detaljer.');
    }
  };

  const handleBulkEksporter = () => {
    const sakerAEksportere = saker.filter(s => valgteSaker.has(s.id));

    const headers = ['Saksnummer', 'Tittel', 'Klient', 'Sakstype', 'Status', 'Opprettet', 'Sist endret'];
    const csvData = sakerAEksportere.map(sak => [
      sak.saksnummer || '',
      sak.tittel || '',
      sak.klient_navn || '',
      sak.sakstype || '',
      sak.status || '',
      sak.opprettet_dato ? new Date(sak.opprettet_dato).toLocaleDateString('nb-NO') : '',
      sak.sist_endret ? new Date(sak.sist_endret).toLocaleDateString('nb-NO') : ''
    ]);

    const csvContent = [
      headers.join(';'),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(';'))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `valgte_saker_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleEksporterCSV = () => {
    // Generer CSV-data
    const headers = ['Saksnummer', 'Tittel', 'Klient', 'Sakstype', 'Status', 'Opprettet', 'Sist endret'];
    const csvData = saker.map(sak => [
      sak.saksnummer || '',
      sak.tittel || '',
      sak.klient_navn || '',
      sak.sakstype || '',
      sak.status || '',
      sak.opprettet_dato ? new Date(sak.opprettet_dato).toLocaleDateString('nb-NO') : '',
      sak.sist_endret ? new Date(sak.sist_endret).toLocaleDateString('nb-NO') : ''
    ]);

    // Kombiner headers og data
    const csvContent = [
      headers.join(';'),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(';'))
    ].join('\n');

    // Last ned som fil
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `saker_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filtrer saker basert på filter-state
  const filtrerteSaker = saker.filter(sak => {
    // Status filter
    if (filter.status !== 'alle' && sak.status !== filter.status) return false;

    // Sakstype filter
    if (filter.sakstype !== 'alle' && sak.sakstype !== filter.sakstype) return false;

    // Søketekst filter
    if (filter.sok) {
      const sok = filter.sok.toLowerCase();
      const matchSaksnummer = sak.saksnummer?.toLowerCase().includes(sok);
      const matchTittel = sak.tittel?.toLowerCase().includes(sok);
      const matchKlient = sak.klient_navn?.toLowerCase().includes(sok);
      const matchMotpart = sak.motpart_navn?.toLowerCase().includes(sok);

      if (!matchSaksnummer && !matchTittel && !matchKlient && !matchMotpart) return false;
    }

    return true;
  });

  // Sorter filtrerte saker
  const filtrerteOgSorterte = [...filtrerteSaker].sort((a, b) => {
    // Arkiverte saker alltid nederst
    if (a.status === 'arkivert' && b.status !== 'arkivert') return 1;
    if (a.status !== 'arkivert' && b.status === 'arkivert') return -1;

    // Sorter etter valgt kriterium
    if (sortering === 'sist_endret') {
      return new Date(b.sist_endret || b.opprettet_dato) - new Date(a.sist_endret || a.opprettet_dato);
    } else if (sortering === 'opprettet') {
      return new Date(b.opprettet_dato) - new Date(a.opprettet_dato);
    } else if (sortering === 'saksnummer') {
      return (a.saksnummer || '').localeCompare(b.saksnummer || '');
    } else if (sortering === 'klient') {
      return (a.klient_navn || '').localeCompare(b.klient_navn || '');
    }
    return 0;
  });

  if (loading) {
    return (
      <div className="sak-liste loading">
        <div className="spinner"></div>
        <p>Laster saker...</p>
      </div>
    );
  }

  return (
    <div className="sak-liste">
      <div className="sak-liste-header">
        <h1>📁 Saker</h1>
        <div style={{display: 'flex', gap: '0.75rem'}}>
          <button
            className="btn-secondary"
            onClick={handleEksporterCSV}
            title="Eksporter sakliste til CSV"
          >
            📊 Eksporter CSV
          </button>
          <button
            className="btn-secondary"
            onClick={() => setVisOppfolgingModal(true)}
            style={{background: '#f59e0b', color: 'white', border: 'none'}}
          >
            📞 Sjekk oppfølging
          </button>
          <button className="btn-primary" onClick={handleNySak}>
            + Ny sak
          </button>
        </div>
      </div>

      {/* Hurtigfiltre */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1rem',
        flexWrap: 'wrap'
      }}>
        <button
          className="btn-secondary"
          onClick={() => {
            setFilter({status: 'aktiv', sakstype: 'alle', sok: ''});
            setSortering('sist_endret');
          }}
          style={{
            padding: '0.5rem 1rem',
            fontSize: '0.9rem',
            background: filter.status === 'aktiv' && filter.sakstype === 'alle' && !filter.sok ? '#3b82f6' : '#f3f4f6',
            color: filter.status === 'aktiv' && filter.sakstype === 'alle' && !filter.sok ? 'white' : '#374151'
          }}
        >
          🔥 Mine aktive saker
        </button>
        <button
          className="btn-secondary"
          onClick={() => {
            setFilter({status: 'alle', sakstype: 'alle', sok: ''});
            setSortering('opprettet');
          }}
          style={{
            padding: '0.5rem 1rem',
            fontSize: '0.9rem',
            background: '#f3f4f6',
            color: '#374151'
          }}
        >
          🆕 Nylig opprettet
        </button>
        <button
          className="btn-secondary"
          onClick={() => {
            const aktiveSaker = saker.filter(s => s.status === 'aktiv' && !s.neste_oppfolging_klient_dato && !s.neste_oppfolging_motpart_dato);
            if (aktiveSaker.length > 0) {
              setFilter({status: 'aktiv', sakstype: 'alle', sok: ''});
              // Vis oppfølgings-modal
              setVisOppfolgingModal(true);
            } else {
              alert('✅ Alle aktive saker har oppfølging!');
            }
          }}
          style={{
            padding: '0.5rem 1rem',
            fontSize: '0.9rem',
            background: '#fef3c7',
            color: '#92400e',
            border: '1px solid #f59e0b'
          }}
        >
          ⚠️ Trenger oppfølging
        </button>
        <button
          className="btn-secondary"
          onClick={() => {
            setFilter({status: 'avsluttet', sakstype: 'alle', sok: ''});
            setSortering('sist_endret');
          }}
          style={{
            padding: '0.5rem 1rem',
            fontSize: '0.9rem',
            background: '#f3f4f6',
            color: '#374151'
          }}
        >
          ✅ Nylig avsluttet
        </button>
        <button
          className="btn-secondary"
          onClick={() => {
            setFilter({status: 'alle', sakstype: 'alle', sok: ''});
            setSortering('sist_endret');
          }}
          style={{
            padding: '0.5rem 1rem',
            fontSize: '0.9rem',
            background: '#f3f4f6',
            color: '#374151'
          }}
        >
          🔄 Nullstill filtre
        </button>
      </div>

      {/* Filtre */}
      <div className="filter-bar">
        <div className="filter-group">
          <label>Søk:</label>
          <input
            type="text"
            placeholder="Søk etter saksnummer, klient, tittel..."
            value={filter.sok}
            onChange={(e) => handleFilterChange('sok', e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <label>Status:</label>
          <select
            value={filter.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="alle">Alle</option>
            <option value="aktiv">Aktive</option>
            <option value="arkivert">Arkiverte</option>
            <option value="avsluttet">Avsluttede</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Sakstype:</label>
          <select
            value={filter.sakstype}
            onChange={(e) => handleFilterChange('sakstype', e.target.value)}
          >
            <option value="alle">Alle typer</option>
            <option value="Arbeidsrett">Arbeidsrett</option>
            <option value="Kontraktsrett">Kontraktsrett</option>
            <option value="Eiendom">Eiendom</option>
            <option value="Familierett">Familierett</option>
            <option value="Straffesak">Straffesak</option>
            <option value="Annet">Annet</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Sorter etter:</label>
          <select
            value={sortering}
            onChange={(e) => setSortering(e.target.value)}
          >
            <option value="sist_endret">Sist endret</option>
            <option value="opprettet">Opprettet dato</option>
            <option value="saksnummer">Saksnummer</option>
            <option value="klient">Klient (alfabetisk)</option>
          </select>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {valgteSaker.size > 0 && (
        <div className="bulk-actions-bar">
          <div className="bulk-info">
            <strong>{valgteSaker.size}</strong> saker valgt
            <button
              className="btn-text"
              onClick={() => setValgteSaker(new Set())}
              style={{marginLeft: '1rem', color: '#6b7280'}}
            >
              Avbryt valg
            </button>
          </div>
          <div className="bulk-actions">
            <button
              className="btn-secondary"
              onClick={() => handleBulkEndreStatus('aktiv')}
              style={{fontSize: '0.9rem', padding: '0.5rem 1rem'}}
            >
              ✅ Sett til aktiv
            </button>
            <button
              className="btn-secondary"
              onClick={() => handleBulkEndreStatus('avsluttet')}
              style={{fontSize: '0.9rem', padding: '0.5rem 1rem'}}
            >
              🏁 Sett til avsluttet
            </button>
            <button
              className="btn-secondary"
              onClick={() => handleBulkEndreStatus('arkivert')}
              style={{fontSize: '0.9rem', padding: '0.5rem 1rem'}}
            >
              📦 Arkiver
            </button>
            <button
              className="btn-primary"
              onClick={handleBulkEksporter}
              style={{fontSize: '0.9rem', padding: '0.5rem 1rem'}}
            >
              📊 Eksporter valgte
            </button>
          </div>
        </div>
      )}

      {/* Statistikk */}
      <div className="sak-stats">
        <span>📊 Viser: {filtrerteOgSorterte.length} av {saker.length} saker</span>
        <span style={{marginLeft: '1rem', color: '#16a34a'}}>
          Aktive: {filtrerteOgSorterte.filter(s => s.status === 'aktiv').length}
        </span>
        <span style={{marginLeft: '1rem', color: '#ea580c'}}>
          Venter: {filtrerteOgSorterte.filter(s => s.status === 'venter').length}
        </span>
        <span style={{marginLeft: '1rem', color: '#0ea5e9'}}>
          Avsluttet: {filtrerteOgSorterte.filter(s => s.status === 'avsluttet').length}
        </span>
        <span style={{marginLeft: '1rem', color: '#94a3b8'}}>
          Arkivert: {filtrerteOgSorterte.filter(s => s.status === 'arkivert').length}
        </span>
      </div>

      {/* Sakliste */}
      {filtrerteOgSorterte.length === 0 ? (
        <div className="empty-state">
          <p>{filter.sok || filter.status !== 'alle' || filter.sakstype !== 'alle' ? 'Ingen saker matchet filteret' : 'Ingen saker funnet'}</p>
          {!filter.sok && filter.status === 'alle' && filter.sakstype === 'alle' && (
            <button className="btn-primary" onClick={handleNySak}>
              Opprett første sak
            </button>
          )}
        </div>
      ) : (
        <div className="saker-tabell">
          <table>
            <thead>
              <tr>
                <th style={{width: '50px'}}>
                  <input
                    type="checkbox"
                    checked={valgteSaker.size === filtrerteOgSorterte.length && filtrerteOgSorterte.length > 0}
                    onChange={handleVelgAlle}
                    title="Velg alle"
                  />
                </th>
                <th>Saksnummer</th>
                <th>Tittel</th>
                <th>Klient</th>
                <th>Sakstype</th>
                <th>Status</th>
                <th>Opprettet</th>
                <th>Handlinger</th>
              </tr>
            </thead>
            <tbody>
              {filtrerteOgSorterte.map(sak => (
                <tr
                  key={sak.id}
                  className="sak-rad"
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={valgteSaker.has(sak.id)}
                      onChange={() => handleVelgSak(sak.id)}
                    />
                  </td>
                  <td className="saksnummer" onClick={() => handleSakKlikk(sak.id)}>
                    {sak.saksnummer}
                    {sak.tripletex_prosjektnummer && (
                      <span style={{fontSize: '0.85rem', opacity: 0.6, marginLeft: '8px'}}>
                        (TT: {sak.tripletex_prosjektnummer})
                      </span>
                    )}
                  </td>
                  <td className="tittel" onClick={() => handleSakKlikk(sak.id)}>{sak.tittel || '-'}</td>
                  <td onClick={() => handleSakKlikk(sak.id)}>{sak.klient_navn}</td>
                  <td onClick={() => handleSakKlikk(sak.id)}>
                    <span className="sakstype-badge">
                      {sak.sakstype || 'Ikke satt'}
                    </span>
                  </td>
                  <td onClick={() => handleSakKlikk(sak.id)}>
                    <span className={`status-badge status-${sak.status}`}>
                      {sak.status}
                    </span>
                  </td>
                  <td onClick={() => handleSakKlikk(sak.id)}>{formatDato(sak.opprettet_dato)}</td>
                  <td className="handlinger" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="btn-icon"
                      onClick={() => handleApneSharePoint(sak)}
                      title="Åpne i SharePoint"
                    >
                      📂
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Oppfølgingskontroll Modal */}
      {visOppfolgingModal && (() => {
        const stats = sjekkOppfolging();
        return (
          <div className="modal-overlay" onClick={() => setVisOppfolgingModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '800px'}}>
              <button className="modal-close" onClick={() => setVisOppfolgingModal(false)}>×</button>
              <h2>📞 Oppfølgingskontroll</h2>

              {/* Statistikk */}
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '2rem'}}>
                <div style={{background: '#f0fdf4', padding: '1.5rem', borderRadius: '8px', border: '2px solid #22c55e'}}>
                  <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#16a34a'}}>{stats.medFullOppfolging}</div>
                  <div style={{color: '#15803d', fontSize: '0.9rem'}}>✅ Med full oppfølging</div>
                </div>
                <div style={{background: '#fef2f2', padding: '1.5rem', borderRadius: '8px', border: '2px solid #ef4444'}}>
                  <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#dc2626'}}>{stats.utenBegge.length}</div>
                  <div style={{color: '#991b1b', fontSize: '0.9rem'}}>⚠️ Uten oppfølging</div>
                </div>
              </div>

              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '2rem'}}>
                <div style={{background: '#fef3c7', padding: '1rem', borderRadius: '8px'}}>
                  <div style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#d97706'}}>{stats.utenKlient.length}</div>
                  <div style={{color: '#92400e', fontSize: '0.85rem'}}>👤 Mangler klient-oppfølging</div>
                </div>
                <div style={{background: '#fef3c7', padding: '1rem', borderRadius: '8px'}}>
                  <div style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#d97706'}}>{stats.utenMotpart.length}</div>
                  <div style={{color: '#92400e', fontSize: '0.85rem'}}>⚖️ Mangler motpart-oppfølging</div>
                </div>
              </div>

              {/* Liste over saker uten oppfølging */}
              {stats.utenBegge.length > 0 && (
                <>
                  <h3 style={{color: '#ef4444', marginBottom: '1rem'}}>⚠️ Saker uten oppfølging ({stats.utenBegge.length})</h3>
                  <div style={{maxHeight: '300px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px'}}>
                    {stats.utenBegge.map(sak => (
                      <div
                        key={sak.id}
                        onClick={() => {
                          setVisOppfolgingModal(false);
                          handleSakKlikk(sak.id);
                        }}
                        style={{
                          padding: '1rem',
                          borderBottom: '1px solid #f3f4f6',
                          cursor: 'pointer',
                          transition: 'background 0.2s',
                          ':hover': {background: '#f9fafb'}
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <div style={{fontWeight: 'bold', color: '#1f2937'}}>
                          {sak.saksnummer ? `#${sak.saksnummer}` : `Sak ${sak.id}`} - {sak.tittel}
                        </div>
                        <div style={{fontSize: '0.85rem', color: '#6b7280', marginTop: '0.25rem'}}>
                          👤 {sak.klient_navn || 'Ukjent klient'}
                        </div>
                        <div style={{fontSize: '0.85rem', color: '#ef4444', marginTop: '0.5rem'}}>
                          {!sak.neste_oppfolging_klient_dato && '❌ Mangler klient-oppfølging  '}
                          {!sak.neste_oppfolging_motpart_dato && '❌ Mangler motpart-oppfølging'}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {stats.utenBegge.length === 0 && (
                <div style={{textAlign: 'center', padding: '2rem', background: '#f0fdf4', borderRadius: '8px'}}>
                  <div style={{fontSize: '3rem'}}>✅</div>
                  <div style={{fontSize: '1.25rem', fontWeight: 'bold', color: '#16a34a', marginTop: '1rem'}}>
                    Alle aktive saker har oppfølging!
                  </div>
                  <p style={{color: '#15803d', marginTop: '0.5rem'}}>
                    Totalt {stats.totaltAktive} aktive saker
                  </p>
                </div>
              )}

              <div style={{marginTop: '2rem', textAlign: 'center', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb'}}>
                <button
                  className="btn-primary"
                  onClick={() => setVisOppfolgingModal(false)}
                >
                  Lukk
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function formatDato(datoString) {
  if (!datoString) return '-';

  const dato = new Date(datoString);
  return dato.toLocaleDateString('nb-NO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}
